import { Button, DialogBody, DialogFooter, FullScreenLoading } from '@civilgrid/ui/components';
import * as Progress from '@radix-ui/react-progress';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { EditorMode, type FeatureArray } from '~/common/types';
import { Notify } from '~/components2/Notify';
import store from '../../../store';
import { maxObjects } from './Import/common';
import fileToGeojson from './Import/fileToGeojson';
import Import from './Import/Import';
import renderObjects from './Import/renderObjects';

interface ImportKmzProps {
  closeDialog: () => void;
}

export const ImportKmz = ({ closeDialog }: ImportKmzProps) => {
  const importRef = useRef<HTMLInputElement>(null);

  // Dialog modal states.
  const [isSelectingFile, setIsSelectingFile] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFailedImport, setIsFailedImport] = useState(false);
  const [isImportComplete, setIsImportComplete] = useState(false);

  // State for progress bar, goes in tandem with "isProcessing" above.
  const [progress, setProgress] = useState(0);

  // Loading spinner state for when rendering the objects
  // Works with "isImportComplete" above.
  const [isLoading, setIsLoading] = useState(false);

  // imported objects
  const [validObjs, setValidObjs] = useState<FeatureArray>([]);
  const [invalidObjs, setInvalidObjs] = useState<FeatureArray>([]);
  const { currentMapId, project } = useSnapshot(store) as typeof store;

  // open file selector as soon as the dialog opens.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    setTimeout(() => {
      // Note, the set timeout is needed to allow the importRef to attach
      // to the invisible "Import" component below.
      openFileSelector();
    }, 0);
  }, []);

  function openFileSelector() {
    if (importRef.current) {
      importRef.current.value = '';
      importRef.current.click();
    }
  }

  async function onFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      setIsSelectingFile(false);
      setIsProcessing(true);
      try {
        const { validObjects, invalidObjects } = await fileToGeojson(e);

        if (validObjects.length === 0) {
          setIsProcessing(false);
          setIsFailedImport(true);
          return;
        }

        if (validObjects.length + invalidObjects.length >= maxObjects) {
          // Added delay on setting failed state so it doesn't 'flash', more pleasant this way.
          setTimeout(() => {
            setIsProcessing(false);
            setIsFailedImport(true);
            Notify.error({
              title: 'KMZ Import Error',
              message: 'The selected KMZ file is too large.',
              // add longer timeout to make it more visible.
              duration: 5000,
            });
          }, 500);
          return;
        }

        setInvalidObjs([...invalidObjects]);
        setValidObjs([...validObjects]);

        setProgress(100);

        // one second delay allows for nice transition.
        setTimeout(() => {
          setIsProcessing(false);
          setIsImportComplete(true);
        }, 1000);
      } catch {
        setIsProcessing(false);
        setIsFailedImport(true);
        return;
      }
    }
  }

  async function render() {
    // cleanup the input value
    importRef.current!.value = '';

    setIsLoading(true);
    try {
      await renderObjects(validObjs);
      setIsLoading(false);
      closeDialog();
      // Below will trigger a re-render with the imported KMZ objects
      // Bit of a hacky way of doing this, probably not the best way.
      store.setEditorMode(EditorMode.DrawLine);
      setTimeout(() => {
        store.setEditorMode(EditorMode.None);
      }, 0);

      if (project.isLoading === false) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawFileImported, {
          project_id: project.id,
          organization_id: project.organization || undefined,
          ...(currentMapId && { map_id: currentMapId }),
        });
      }
    } catch {
      setIsLoading(false);
      setIsFailedImport(true);
      return;
    }
  }

  return (
    <>
      <DialogBody className="text-sm">
        {isSelectingFile && <FileSelection />}
        {isProcessing && <Processing progress={progress} />}
        {isFailedImport && <FailedImport />}
        {isImportComplete && <ImportComplete invalidObjs={invalidObjs} />}
      </DialogBody>
      <DialogFooter>
        {/* Always show "Close" button */}
        <CancelButton importRef={importRef} onCancel={closeDialog} />

        {/* Only show "Select File" if first opened or no file is selected */}
        {!isProcessing && !isImportComplete && !isFailedImport && (
          <SelectFileButton openFileSelector={openFileSelector} />
        )}

        {/* Only show the "Add Objects" button if a valid kmz was uploaded */}
        {isImportComplete && <Button onClick={() => render()}>{`Add ${validObjs.length} Objects`}</Button>}
      </DialogFooter>
      <Import ref={importRef} onChange={onFileUpload} accept=".kmz, .kml" />
      {isLoading && <FullScreenLoading />}
    </>
  );
};

const FileSelection = () => {
  return <span>Select file to upload.</span>;
};

const Processing = ({ progress }: { progress: number }) => {
  return (
    <div className="flex flex-col items-center">
      <div>{progress}%</div>
      <Progress.Root
        value={progress}
        className="relative h-6 w-72 overflow-hidden rounded-full bg-brand"
        style={{
          // Fix overflow skipping in safari per link below:
          // https://www.radix-ui.com/docs/primitives/components/progress
          transform: 'translateZ(0)',
        }}
      >
        <Progress.Indicator
          className="ease-[cubic-bezier(0.65, 0, 0.35, 1)] h-full w-full bg-primary transition-transform duration-[660ms]"
          style={{
            // translate not working with tailwind, maybe because @tailwind:base not implemented yet?
            transform: `translate(-${100 - progress}%)`,
          }}
        ></Progress.Indicator>
      </Progress.Root>
    </div>
  );
};

const ImportComplete = ({ invalidObjs }: { invalidObjs: FeatureArray }) => {
  if (invalidObjs.length > 0) {
    return (
      <>
        <div>
          The following {invalidObjs.length} objects could not be imported because they don't have a geometry associated
          with them.
        </div>
        <ul className="max-h-[200px] min-h-[100px] w-full overflow-y-auto overflow-x-hidden">
          {invalidObjs.map((obj, index) => {
            return (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={`KEY: ${index}`}
              >
                <span>{obj.properties?.name}</span>
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  return <div>All done!</div>;
};

const FailedImport = () => {
  return (
    <div>
      CivilDraw could not load this KMZ. Please try again or contact{' '}
      <a
        href="mailto:support@civilgrid.com"
        className="text-blue-50 underline hover:text-blue-60"
        rel="noopener noreferrer"
      >
        support
      </a>{' '}
      for further assistance.
    </div>
  );
};

// Buttons
const SelectFileButton = ({ openFileSelector }: { openFileSelector: () => void }) => {
  return <Button onClick={() => openFileSelector()}>Select File</Button>;
};

interface CancelButtonProps {
  importRef: React.RefObject<HTMLInputElement | null>;
  onCancel: () => void;
}

const CancelButton = ({ importRef, onCancel }: CancelButtonProps) => {
  return (
    <Button
      variant="secondary"
      onClick={() => {
        onCancel();
        if (importRef.current) {
          importRef.current.value = '';
        }
      }}
    >
      Close
    </Button>
  );
};
